import { defineStore } from 'pinia';
import { v4 as uuidv4 } from 'uuid';
import { isRef, type MaybeRef, ref } from 'vue';
import type { ToastProps, ToastVariantType } from '@tundr/ds-components';
import { getToastDurationByVariant } from '../utils/toast-duration.ts';

export type ToastItemProps = ToastProps & {
  distinctId: string;
  variant: ToastVariantType;
};

type ToastStoreProps = ToastItemProps & {
  timeoutId: ReturnType<typeof setTimeout>;
};

export type ToastPropsWithOptionalDistinctId = Omit<
  ToastItemProps,
  'distinctId'
> &
  Partial<Pick<ToastItemProps, 'distinctId'>>;

export const useToastStore = defineStore('toast', () => {
  const items = ref<Map<string, ToastStoreProps>>(new Map());

  const removeItem = (item: ToastStoreProps) => {
    items.value.delete(item.distinctId);
    clearTimeout(item.timeoutId);
  };

  const setItem = (
    item: MaybeRef<ToastPropsWithOptionalDistinctId>,
    timeout?: number,
  ) => {
    const rawItem = isRef(item) ? item.value : item;
    const computedItem: ToastItemProps = {
      ...rawItem,
      distinctId: rawItem.distinctId ?? uuidv4(),
    };

    const hasToDistinct = items.value.get(computedItem.distinctId);
    if (hasToDistinct) return;

    const effectiveTimeout =
      timeout ?? getToastDurationByVariant(computedItem.variant);

    const timeoutId = setTimeout(() => {
      removeItem({ ...computedItem, timeoutId });
    }, effectiveTimeout);

    items.value.set(computedItem.distinctId, { ...computedItem, timeoutId });
  };

  return {
    items,
    removeItem,
    setItem,
  };
});
